// Protected route wrapper - redirects to auth if not logged in
import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Inject noindex meta tag for protected routes (SEO: prevent indexing auth-walled pages)
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"][data-protected-route]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      meta.setAttribute('data-protected-route', 'true');
      document.head.appendChild(meta);
    }
    meta.content = 'noindex, nofollow';

    return () => {
      const tag = document.querySelector('meta[name="robots"][data-protected-route]');
      if (tag) tag.remove();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to auth page, preserving the intended destination
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
